import React, {useMemo} from 'react';
import {classes, style, vars} from './doppe-layout6-profile-view-template.st.css';
import {BymoTemplateMetadataInferBymoPageProps, useBymoPageContextProps} from '../../../doppe-sdk';
import {RatioMaintainerIgnoreClassName} from '@wix/devzai-utils-react';
import {arrayFirst, arrayGenerate} from '@wix/devzai-utils-common';
import {FlexLayout} from '@wix/devzai-common-client';
import {doppeHideableValueIsVisible} from '../../../client-server-common/types/doppe-hideable-value';
import {DoppeHideableValueRenderer} from '../../doppe-hideable-value-renderer/doppe-hideable-value-renderer';
import {
    DoppePageProfileNameView,
    DoppePageProfileNameViewVerifiedCheckAppearance
} from '../../doppe-page-profile-name-view/doppe-page-profile-name-view';
import {DoppeProfileNameAutoSource} from '../../../client-server-common';
import {
    BymoTemplateSpeedDialTemplateMetadata
} from '../../bymo-templates/bymo-template-speed-dial/bymo-template-speed-dial.metadata';
import {DoppeLayoutRoot} from '../dopp-layouts-components/doppe-layout-root/doppe-layout-root';
import {DoppLayoutContent} from '../dopp-layouts-components/dopp-layout-content/dopp-layout-content';
import {DoppLayoutBackground} from '../dopp-layouts-components/dopp-layout-background/dopp-layout-background';
import {
    DoppLayoutBackgroundMedia
} from '../dopp-layouts-components/dopp-layout-background-media/dopp-layout-background-media';
import {DoppePageProfilePicture} from '../../doppe-page-profile-picture/doppe-page-profile-picture';
import {DoppeBackgroundView} from '../../doppe-background-view/doppe-background-view';
import {doppeViewerShouldRemoveHoppBanner} from '../../doppe-viewer-utils/doppe-viewer-utils';
import {
    DoppLayoutJumpingArrow,
    DoppLayoutJumpingArrowSkin
} from '../dopp-layouts-components/dopp-layout-jumping-arrow/dopp-layout-jumping-arrow';
import {useDoppeViewerClientServices} from '../../../client/doppe-user-app/doppe-viewer-client-services';
import {DoppeLayoutPageContent} from '../dopp-layouts-components/doppe-layout-page-content/doppe-layout-page-content';
import {DoppeLayoutBioText} from '../dopp-layouts-components/doppe-layout-bio-text/doppe-layout-bio-text';
import {DoppeLayoutSocialLinks} from '../dopp-layouts-components/doppe-layout-social-links/doppe-layout-social-links';


export const DoppeLayout6ProfileViewTemplate = React.memo(function DoppeLayout1ProfileView(props: BymoTemplateMetadataInferBymoPageProps<typeof BymoTemplateSpeedDialTemplateMetadata>) {
        const {
            profilePictureLinkInBio,
            bioText,
            bioTextMaxLines,
            socialLinks,
            heroPhotos,
            heroPhotosOpacity,
            profileName,
        } = props;


        const contextProps = useBymoPageContextProps();
        const doppeViewerClientServices = useDoppeViewerClientServices();

        const hasProfileImage = useMemo(() => {
            return doppeHideableValueIsVisible(profilePictureLinkInBio);
        }, [profilePictureLinkInBio]);


        const removeHoppBanner = doppeViewerShouldRemoveHoppBanner(props, contextProps);

        return (
            <DoppeLayoutRoot
                className={style(classes.root, {
                    withProfileImage: hasProfileImage,
                })}
                style={{
                    [vars.opacity]: `${heroPhotosOpacity}%`,
                    [vars.footerBannerHeight]: `${removeHoppBanner ? 0 : 51}px`
                }}
                hideHoppBanner={props.removeHoppBanner}
            >
                <DoppLayoutBackground
                    className={style(classes.coverBackground)}
                >
                    <div
                        className={style(classes.heroPhotoContainer, {})}
                    >
                        <DoppeBackgroundView
                            className={style(classes.heroPhoto)}
                            heroPhoto={arrayFirst(heroPhotos ?? [])}
                        />
                        <div className={style(classes.heroPhotoOverlay, {})}/>
                        {doppeHideableValueIsVisible(profileName) ? (
                            <div
                                className={style(classes.animatedProfileNameContainer, {}, FlexLayout.row.alignToCenter, RatioMaintainerIgnoreClassName)}
                            >
                                {arrayGenerate(5).map(index => {
                                    return (
                                        <div
                                            key={index}
                                            className={style(classes.profileName, {}, FlexLayout.row.alignToCenter)}
                                        >
                                            <DoppePageProfileNameView
                                                defaultProfileNameAutoSource={DoppeProfileNameAutoSource.AccountName}
                                                verifiedCheckAppearance={DoppePageProfileNameViewVerifiedCheckAppearance.Superscript}
                                            />
                                            <div
                                                className={style(classes.decorator, {})}
                                            >
                                                {`⭑`}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        ) : null}
                        <div
                            className={style(classes.profileView, FlexLayout.column.alignToCenter)}
                        >
                            {
                                hasProfileImage ?
                                    <DoppeHideableValueRenderer
                                        hideableValue={profilePictureLinkInBio}
                                        renderFunction={profilePicture => (
                                            <DoppePageProfilePicture
                                                className={style(classes.profileImage, {})}
                                                profilePicture={profilePicture}
                                                size={140}
                                            />
                                        )}
                                    />
                                    :
                                    null
                            }
                            <DoppeLayoutBioText
                                className={style(classes.bioText, {})}
                                bioText={bioText}
                                bioTextMaxLines={bioTextMaxLines}
                                lineHeight={'18px'}
                            />
                            <DoppeLayoutSocialLinks
                                className={style(classes.socialLinks, {}) }
                                socialLinks={socialLinks}
                            />
                            <div
                                className={style(classes.downButtonContainer, {}, FlexLayout.centerContent)}
                            >
                                <DoppLayoutJumpingArrow
                                    className={style(classes.downButton, {})}
                                    skin={DoppLayoutJumpingArrowSkin.Small}
                                    onClick={async ()=> {
                                        await doppeViewerClientServices.uiService.pageActionsList?.scrollToActionsListContainer();
                                    }}
                                />
                            </div>

                        </div>
                    </div>
                    <DoppLayoutBackgroundMedia
                        className={style(classes.pageWithCoverBackgroundView, {})}
                    />
                </DoppLayoutBackground>
                <DoppLayoutContent
                    className={style(classes.content)}
                >
                    <DoppeLayoutPageContent
                        pageProps={props}
                    />
                </DoppLayoutContent>
            </DoppeLayoutRoot>
        )
    })
;

